import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { money } from './drawerFormat.js';
import { VARIANCE_ACTION_LABELS, VARIANCE_SOURCE_LABELS } from './varianceActions.js';

const actionRouteLabel = (action) => {
  const sourceLabel = action.sourceLabel || VARIANCE_SOURCE_LABELS[action.source] || 'Source';
  const targetLabel = action.targetLabel || VARIANCE_SOURCE_LABELS[action.targetSource];
  if (targetLabel) return `${sourceLabel} -> ${targetLabel}`;
  return sourceLabel;
};

const actionLabel = (action) => action.actionLabel || VARIANCE_ACTION_LABELS[action.actionType] || action.actionType || 'Variance action';

const exportLaneLabel = (lane) => {
  if (lane === 'gm_correction') return 'GM correction';
  if (lane === 'cpq_peoplesoft_adjustment') return 'CPQ/PeopleSoft';
  return 'Audit only';
};

const productNoteStamp = (action = {}) => {
  const note = action.metadata?.productNote;
  if (!note?.text) return null;
  const code = note.productCode ? ` - ${note.productCode}` : '';
  const effectiveDate = note.effectiveDate ? ` - ${note.effectiveDate}` : '';
  return `${note.author || 'Unknown'}${code}${effectiveDate}`;
};

const productNotesForActions = (actions = []) => actions
  .map((action) => ({ action, note: action.metadata?.productNote }))
  .filter(({ note }) => note?.text);

const actionStatusMeta = (action = {}) => {
  const status = action.saveStatus || action.status || 'draft';
  if (status === 'resolved') return { label: 'Resolved', color: 'success' };
  if (status === 'exported') return { label: 'Exported', color: 'info' };
  if (status === 'saved') return { label: 'Saved', color: 'success' };
  return { label: 'Draft', color: 'warning' };
};

const ProductNoteRollup = ({ actions = [] }) => {
  const notes = productNotesForActions(actions);
  if (!notes.length) return null;
  return (
    <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1, bgcolor: 'action.hover' }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 900, display: 'block', mb: 0.75 }}>
        Product investigation feed
      </Typography>
      <Stack spacing={0.75}>
        {notes.map(({ action, note }) => (
          <Box key={`${action.id}-product-note`}>
            <Typography variant="caption" sx={{ fontWeight: 800, display: 'block' }}>
              {note.productCode || action.productCode || 'Product'} - {note.author || action.createdByName || 'Unknown'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
              {[note.effectiveDate].filter(Boolean).join(' - ') || 'Product note'}: {note.text}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
};

const DpeActionRows = ({ actions = [], onRemoveDpe = null }) => {
  if (!actions.length) {
    return (
      <Box sx={{ p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Drag an AppSheet card onto DPE to add, or drag a DPE card to the remove lane to queue DPE work.
        </Typography>
      </Box>
    );
  }
  return (
    <Stack spacing={1}>
      {actions.map((action) => (
        <Paper key={action.id} variant="outlined" sx={{ p: 1.25, borderRadius: 1, borderLeft: '4px solid', borderLeftColor: 'info.main' }}>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 900 }}>
                  {action.actionType === 'remove' ? 'Remove from DPE' : action.actionType === 'change_price' ? 'Change DPE price' : 'Add to DPE'}
                </Typography>
                <Chip size="small" label={action.queueStatus || 'draft'} color={action.queueStatus === 'sent' ? 'success' : 'info'} variant="outlined" sx={{ height: 18, '& .MuiChip-label': { px: 0.65, fontSize: 10, fontWeight: 800 } }} />
              </Stack>
              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                {action.productDescription || action.vmtPayload?.productDescription || 'Product'} - {action.productCd || action.vmtPayload?.productCd || '--'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                Ref {action.referenceId || action.vmtPayload?.referenceId || '--'} - {money(action.dealerPrice ?? action.vmtPayload?.dealerPrice)} - {action.productStatus || action.vmtPayload?.status || '--'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                Effective {action.effectiveDate || action.vmtPayload?.effectiveDate || '--'} - Last updated {action.lastUpdatedDate || action.vmtPayload?.lastUpdatedDate || '--'}
              </Typography>
            </Box>
            {onRemoveDpe && action.queueStatus !== 'sent' ? (
              <Tooltip title="Remove DPE action">
                <IconButton size="small" onClick={() => onRemoveDpe(action.id)} aria-label="Remove DPE action">
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
};

const actionMatchesFocus = (action = {}, focusActionId = '') => {
  if (!focusActionId) return false;
  const target = String(focusActionId);
  return [action.id, action.actionId].filter(Boolean).some((id) => String(id) === target);
};

const VarianceAuditPanel = ({ actions = [], dpeActions = [], onRemove, onRemoveDpe = null, onPromoteRecurring = null, focusActionId = '' }) => {
  const focusRef = useRef(null);
  const [activeTab, setActiveTab] = useState('variance');

  useEffect(() => {
    if (!focusActionId || !focusRef.current) return;
    focusRef.current.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  }, [focusActionId, actions]);

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
      <Stack spacing={1.5}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
        <Box>
          <Typography
            variant="overline"
            sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.6, fontSize: 10.5, display: 'block' }}
          >
            Variance audit
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Product-level draft actions for this BAC.
          </Typography>
        </Box>
        <Chip size="small" label={`${actions.length} change${actions.length === 1 ? '' : 's'}`} color={actions.length ? 'primary' : 'default'} />
      </Stack>
      <Tabs value={activeTab} onChange={(_event, value) => setActiveTab(value)} sx={{ minHeight: 34, '& .MuiTab-root': { minHeight: 34, textTransform: 'none', fontWeight: 800 } }}>
        <Tab value="variance" label={`Variance actions ${actions.length}`} />
        <Tab value="dpe" label={`DPE actions ${dpeActions.length}`} />
      </Tabs>
      {activeTab === 'dpe' ? (
        <DpeActionRows actions={dpeActions} onRemoveDpe={onRemoveDpe} />
      ) : actions.length === 0 ? (
        <Box sx={{ p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Right-click a product, or long-press on mobile, to start a variance action.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1}>
          <ProductNoteRollup actions={actions} />
          {actions.map((action) => {
            const focused = actionMatchesFocus(action, focusActionId);
            const statusMeta = actionStatusMeta(action);
            return (
            <Paper
              key={action.id}
              ref={focused ? focusRef : null}
              variant="outlined"
              draggable
              onDragStart={(event) => {
                event.dataTransfer.effectAllowed = 'copy';
                event.dataTransfer.setData('application/x-billing-audit-action', JSON.stringify({ id: action.id }));
                event.dataTransfer.setData('text/plain', `[audit Link:${action.id}]`);
              }}
              sx={{
                p: 1.25,
                borderRadius: 1,
                borderLeft: '4px solid',
                borderLeftColor: focused ? 'warning.main' : 'primary.main',
                bgcolor: focused ? 'rgba(255, 193, 7, 0.12)' : 'background.paper',
                boxShadow: focused ? 2 : 0,
              }}
              data-billing-action-id={action.id}
            >
              <Stack direction="row" alignItems="flex-start" spacing={1}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mb: 0.75 }}>
                    <Typography variant="body2" sx={{ fontWeight: 900, mr: 0.25 }}>
                      {actionLabel(action)}
                    </Typography>
                    <Chip
                      size="small"
                      label={statusMeta.label}
                      color={statusMeta.color}
                      variant="outlined"
                      sx={{ height: 18, '& .MuiChip-label': { px: 0.65, fontSize: 10, fontWeight: 800 } }}
                    />
                    <Chip
                      size="small"
                      label={actionRouteLabel(action)}
                      variant="outlined"
                      sx={{ height: 18, '& .MuiChip-label': { px: 0.65, fontSize: 10, fontWeight: 700 } }}
                    />
                    <Chip
                      size="small"
                      label={exportLaneLabel(action.exportLane)}
                      variant="outlined"
                      sx={{ height: 18, '& .MuiChip-label': { px: 0.65, fontSize: 10, fontWeight: 700 } }}
                    />
                  </Stack>
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    {action.productName || 'Product'} {action.productCode ? `- ${action.productCode}` : ''}
                    {action.price || action.price === 0 ? ` - ${money(action.price)}` : ''}
                  </Typography>
                  {action.replacementProductCode && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                      Replace with {action.replacementProductCode}
                    </Typography>
                  )}
                  {(action.proposedPrice || action.proposedPrice === 0) && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                      Proposed price {money(action.proposedPrice)}
                    </Typography>
                  )}
                  {(action.manualOrderItemId || action.manualReferenceId) && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                      Evidence override:
                      {action.manualOrderItemId ? ` AppSheet order item ${action.manualOrderItemId}` : ''}
                      {action.manualOrderItemId && action.manualReferenceId ? ' -' : ''}
                      {action.manualReferenceId ? ` DPE ref ${action.manualReferenceId}` : ''}
                    </Typography>
                  )}
                  {(action.sourceProductName || action.sourceProductCode) && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                      Inherits from {action.sourceProductName || 'source product'}{action.sourceProductCode ? ` - ${action.sourceProductCode}` : ''}
                    </Typography>
                  )}
                  {action.problem && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.75 }}>
                      Problem: {action.problem}
                    </Typography>
                  )}
                  {action.fix && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                      Fix: {action.fix}
                    </Typography>
                  )}
                  {action.note && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                      Note: {action.note}
                    </Typography>
                  )}
                  {action.metadata?.productNote?.text && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                      Product note ({productNoteStamp(action)}): {action.metadata.productNote.text}
                    </Typography>
                  )}
                </Box>
                <Stack spacing={0.5}>
                  {onPromoteRecurring && action.actionType === 'force_evidence_match' ? (
                    <Tooltip title="Promote as recurring match rule">
                      <IconButton size="small" onClick={() => onPromoteRecurring(action.id)} aria-label="Promote recurring match">
                        <AutoAwesomeOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                  <Tooltip title="Remove draft variance">
                    <IconButton size="small" onClick={() => onRemove(action.id)} aria-label="Remove draft variance">
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
            </Paper>
            );
          })}
        </Stack>
      )}
    </Stack>
  </Paper>
  );
};

export default VarianceAuditPanel;
